import { unpack } from "./decoder";
import type { DecodedZone } from "./types";
import { tzdata } from "./tzdata.generated";

const packedZones = new Map(
	tzdata.zones.map((packed) => {
		const separator = packed.indexOf("|");
		return [packed.slice(0, separator), packed] as const;
	}),
);
const links = new Map(
	tzdata.links.map((link) => {
		const [target, alias] = link.split("|");
		return [alias!, target!] as const;
	}),
);
const decodedZones = new Map<string, DecodedZone>();
const truncatedZones = new Set(tzdata.truncated);

// The last entry is open-ended, so the last real transition is the one before it.
const lastTransition = (zone: DecodedZone) => zone.untils[zone.untils.length - 2] ?? Number.POSITIVE_INFINITY;

const resolvePackedZone = (zoneName: string) => {
	const seen = new Set<string>();
	let current = zoneName;

	while (!seen.has(current)) {
		seen.add(current);
		const direct = packedZones.get(current);
		if (direct) {
			return direct;
		}
		const target = links.get(current);
		if (!target) {
			return undefined;
		}
		current = target;
	}
	return undefined;
};

export const isShippedZone = (zoneName: string) => packedZones.has(zoneName) || links.has(zoneName);

export const lookupZone = (zoneName: string) => {
	const cached = decodedZones.get(zoneName);
	if (cached) {
		return cached;
	}
	const packed = resolvePackedZone(zoneName);
	if (!packed) {
		return undefined;
	}
	const decoded = unpack(packed);
	decodedZones.set(zoneName, decoded);
	return decoded;
};

export const shippedZoneNames = [...packedZones.keys(), ...links.keys()];

export const negateOffset = (minutes: number) => (minutes === 0 ? 0 : -minutes);

export const tzdataVersion = tzdata.version;

export const getTimezoneOffset = ({
	instant,
	zoneName,
}: {
	instant: Date | number;
	zoneName: string;
}) => {
	const zone = lookupZone(zoneName);
	if (!zone) {
		return undefined;
	}
	const timestampMs = typeof instant === "number" ? instant : instant.getTime();

	// Outside the generated window the runtime answers better than this artifact does: it holds rules
	// and extrapolates them, while this only enumerates transitions. A zone that genuinely stopped
	// changing is the exception, because there its last entry is the rule rather than a cutoff.
	if (timestampMs < tzdata.coverageStart) {
		return undefined;
	}
	if (truncatedZones.has(zoneName) && timestampMs >= lastTransition(zone)) {
		return undefined;
	}

	// Packed moment offsets are minutes to subtract; callers need minutes to add.
	return negateOffset(zone.utcOffset(timestampMs));
};
